import * as React from 'react';
import { inject } from 'mobx-react';
import { Observer, observer } from 'mobx-react-lite';
import { Alert, Button, Layout, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import FlexContentContainer from '../../common/components/FlexContentContainer';
import LayoutHeader from '../../../components/LayoutHeader';
import { STORE_API_KEYS } from '../constants';
import ApiKeyEditorModal from '../components/ApiKeyEditorModal';
import ApiKeySecretModal from '../components/ApiKeySecretModal';
import { AdministrationStores } from '../stores';
import { ApiKey } from '../types';

type Props = AdministrationStores;

const { Text } = Typography;

enum ApiKeyStatus {
    Enabled = 'Enabled',
    Disabled = 'Disabled',
    Expired = 'Expired',
    Revoked = 'Revoked'
}

const apiKeyStatuses = Object.values(ApiKeyStatus);
const defaultApiKeyStatuses = [ApiKeyStatus.Enabled, ApiKeyStatus.Disabled, ApiKeyStatus.Expired];

const formatDate = (value: string | null, emptyLabel: string) => {
    if (!value) {
        return emptyLabel;
    }

    const dateTime = DateTime.fromISO(value);
    return dateTime.isValid ? dateTime.toLocaleString(DateTime.DATETIME_MED) : value;
};

const getApiKeyStatus = (apiKey: ApiKey) => {
    if (apiKey.revokedAt) {
        return { label: ApiKeyStatus.Revoked, color: 'red' };
    }

    if (!apiKey.enabled) {
        return { label: ApiKeyStatus.Disabled, color: 'default' };
    }

    if (apiKey.expiresAt && Date.parse(apiKey.expiresAt) <= Date.now()) {
        return { label: ApiKeyStatus.Expired, color: 'orange' };
    }

    return { label: ApiKeyStatus.Enabled, color: 'green' };
};

const ApiKeysPage: React.FC<Props> = ({ ApiKeys: store }) => {
    const [selectedStatuses, setSelectedStatuses] = React.useState<ApiKeyStatus[]>(defaultApiKeyStatuses);

    React.useEffect(() => {
        if (!store) {
            return;
        }

        store.initialize();
        return () => store.dispose();
    }, [store]);

    if (!store) {
        return null;
    }

    const filteredApiKeys = store.apiKeys.filter(apiKey => selectedStatuses.includes(getApiKeyStatus(apiKey).label));

    const columns: ColumnProps<ApiKey>[] = [
        {
            title: 'Key name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, apiKey: ApiKey) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {apiKey.description ? <Text type="secondary">{apiKey.description}</Text> : null}
                </Space>
            )
        },
        {
            title: 'Integration name',
            dataIndex: 'integrationName',
            key: 'integrationName'
        },
        {
            title: 'Scopes',
            dataIndex: 'scopes',
            key: 'scopes',
            render: (scopes: string[]) => (
                <Space size="small" wrap>
                    {scopes.map(scope => (
                        <Tag key={scope}>{scope}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: 'Projects',
            dataIndex: 'allowedProjectIds',
            key: 'projects',
            render: (projectIds: string[]) => (
                <Observer>
                    {() => {
                        if (projectIds.length === 0) {
                            return <Tag color="orange">No project access</Tag>;
                        }

                        return (
                            <span>
                                {projectIds
                                    .map(id => store.projects.find(project => project.id === id)?.name ?? id)
                                    .join(', ')}
                            </span>
                        );
                    }}
                </Observer>
            )
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, apiKey: ApiKey) => {
                const status = getApiKeyStatus(apiKey);
                return <Tag color={status.color}>{status.label}</Tag>;
            }
        },
        {
            title: 'Last used',
            key: 'lastUsed',
            render: (_: unknown, apiKey: ApiKey) => (
                <Space direction="vertical" size={0}>
                    <span>{formatDate(apiKey.lastUsedAt, 'Never')}</span>
                    {apiKey.lastUsedFromIp ? <Text type="secondary">{apiKey.lastUsedFromIp}</Text> : null}
                </Space>
            )
        },
        {
            title: 'Created',
            key: 'created',
            render: (_: unknown, apiKey: ApiKey) => (
                <Space direction="vertical" size={0}>
                    <span>{formatDate(apiKey.createdAt, 'Unknown')}</span>
                    <Observer>{() => <Text type="secondary">{store.getUserNameById(apiKey.createdBy)}</Text>}</Observer>
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, apiKey: ApiKey) => {
                if (apiKey.revokedAt) {
                    return <Text type="secondary">—</Text>;
                }

                return (
                    <Space wrap>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            aria-label={`Edit ${apiKey.name}`}
                            disabled={store.isBusy}
                            onClick={() => store.openEditModal(apiKey)}
                        >
                            Edit
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            disabled={store.isBusy}
                            onClick={() => store.setEnabled(apiKey, !apiKey.enabled)}
                            aria-label={`${apiKey.enabled ? 'Disable' : 'Enable'} ${apiKey.name}`}
                        >
                            {apiKey.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Popconfirm
                            title="The current key will stop working. Regenerate it?"
                            okText="Regenerate"
                            cancelText="Cancel"
                            disabled={store.isBusy}
                            onConfirm={() => store.regenerate(apiKey)}
                        >
                            <Button
                                type="link"
                                size="small"
                                disabled={store.isBusy}
                                aria-label={`Regenerate ${apiKey.name}`}
                            >
                                Regenerate
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="This API key cannot be restored. Revoke it?"
                            okText="Revoke"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                            disabled={store.isBusy}
                            onConfirm={() => store.revoke(apiKey)}
                        >
                            <Button
                                type="link"
                                size="small"
                                danger
                                disabled={store.isBusy}
                                aria-label={`Revoke ${apiKey.name}`}
                            >
                                Revoke
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    const content = (
        <Layout className="screen-size" style={{ height: '100%', background: '#fff' }}>
            <LayoutHeader
                title="API Keys"
                buttons={[
                    <Button
                        key="refresh-api-keys"
                        size="large"
                        icon={<ReloadOutlined />}
                        disabled={store.isBusy}
                        loading={store.isLoading}
                        onClick={store.initialize}
                    >
                        Refresh
                    </Button>,
                    <Button
                        key="create-api-key"
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        disabled={store.isBusy || store.hasLoadError}
                        onClick={store.openCreateModal}
                    >
                        Create API key
                    </Button>
                ]}
            />
            <Layout.Content style={{ padding: '0 0 16px', overflow: 'auto' }}>
                {store.hasLoadError && (
                    <Alert
                        type="error"
                        showIcon
                        message="Could not load API keys. Try again."
                        action={
                            <Button onClick={store.initialize} disabled={store.isBusy} loading={store.isLoading}>
                                Retry
                            </Button>
                        }
                    />
                )}
                <Select
                    data-id="api-keys-status-filter"
                    aria-label="Filter API keys by status"
                    mode="multiple"
                    style={{ width: 350, margin: '16px 0 8px' }}
                    placeholder="Status..."
                    value={selectedStatuses}
                    onChange={(statuses: ApiKeyStatus[]) => setSelectedStatuses(statuses)}
                    options={apiKeyStatuses.map(status => ({ label: status, value: status }))}
                />
                <Table<ApiKey>
                    rowKey="id"
                    loading={store.isLoading}
                    columns={columns}
                    dataSource={filteredApiKeys}
                    pagination={false}
                    locale={{
                        emptyText: store.hasLoadError
                            ? 'API keys could not be loaded.'
                            : 'No API keys have been created.'
                    }}
                    scroll={{ x: true }}
                />
            </Layout.Content>

            <ApiKeyEditorModal store={store} />
            <ApiKeySecretModal rawKey={store.rawKey} onClose={store.clearRawKey} />
        </Layout>
    );

    return <FlexContentContainer content={content} contentStyle={{ overflow: 'hidden' }} />;
};

export default inject(STORE_API_KEYS)(observer(ApiKeysPage));
